import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { getAppAndSiteMarketplaceTags } from '@/components/common/AppAndSiteMarketplace/helpers/getSectionHeaderTags.helper';
import { getAppAndSiteChannelTags } from '@/components/Targeting/AppAndSiteChannel/helpers/getSectionHeaderTags.helper';
import { getAudienceTargetingTags } from '@/components/Targeting/AudienceTargeting/helpers/getSectionHeaderTags.helper';
import { getDemographyTags } from '@/components/Targeting/Demography/helpers/getSectionHeaderTags.helper';
import { getDeviceTargetingTags } from '@/components/Targeting/DeviceTargeting/helpers/getSectionHeaderTags.helper';
import { getLQITags } from '@/components/Targeting/LQIQualityAssurance/helpers/getSectionHeaderTags.helper';
import { getMediaQualityTags } from '@/components/Targeting/MediaQualityAssuranceSystem/helpers/getSectionHeaderTags.helper';
import { getMobileDeviceTags } from '@/components/Targeting/MobileDeviceAndConnectivity/helpers/getSectionHeaderTags.helper';
import { getSupplySourceTags } from '@/components/Targeting/SupplySource/helpers/getSectionHeaderTags.helper';
import { getTemperatureAndWeatherTags } from '@/components/Targeting/TemperatureAndWeather/helpers/getSectionHeaderTags.helper';
import { isDoohAudienceConfigured } from '@/helpers/doohAudience/doohAudience.helper';

import type { ValidationErrors } from 'sonata-react-components';
import type { ContextualBiddingInterface } from 'sonata-static-storage/lib/schemas/campaign/contextualBidding.schema';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { DoohAudience } from '@/services/doohAudience.service';
import type { SidebarChildItemConfig } from '../sidebar.types';

type DotStatus = SidebarChildItemConfig['dotStatus'];

type DotStatusesByTargeting = {
  appMarketplace: DotStatus | null;
  appSiteCategories: DotStatus | null;
  appSiteChannel: DotStatus | null;
  audienceTargeting: DotStatus | null;
  contentCategories: DotStatus | null;
  contextualBidding: DotStatus | null;
  demography: DotStatus | null;
  deviceTargeting: DotStatus | null;
  locationQualityIndexes: DotStatus | null;
  mediaQualityAssurance: DotStatus | null;
  mobileDevicesConnectivity: DotStatus | null;
  retargeting: DotStatus | null;
  supplySource: DotStatus | null;
  temperatureAndWeather: DotStatus | null;
};

export function getDotStatusesByTargeting(
  {
    adExchanges,
    ages,
    appSiteConfiguration,
    audience,
    categories,
    channels,
    connectionTypes,
    contextualTargeting,
    deals,
    deviceOsV2,
    deviceTypes,
    gender,
    languages,
    lqi,
    mediaQuality,
    retargeting,
    targetUsers,
    temperatureAndWeather
  }: TargetingBaseType,
  contextualBidding?: ContextualBiddingInterface<any>,
  type?: CampaignFamilyEnum,
  errors?: ValidationErrors,
  doohAudience?: DoohAudience
): DotStatusesByTargeting {
  const checkIfConfigured = (tags: unknown[]) => {
    return tags.length > 0;
  };

  const withStatus = (isConfigured: boolean, ...errorKeys: string[]): DotStatus => {
    if (!isConfigured) {
      return null;
    }
    if (errors && errorKeys.some(k => errors[k] !== undefined)) {
      return 'warning';
    }
    return 'success';
  };

  const baseDotStatuses = {
    appMarketplace: withStatus(
      checkIfConfigured(getAppAndSiteMarketplaceTags(appSiteConfiguration, deals)),
      'appSiteConfiguration',
      'deals'
    ),
    appSiteCategories: null,
    appSiteChannel: null,
    audienceTargeting: null,
    contentCategories: null,
    contextualBidding: withStatus(
      Boolean(contextualBidding?.enabled && contextualBidding?.scenarios.length),
      'contextualBidding'
    ),
    demography: null,
    deviceTargeting: null,
    locationQualityIndexes: null,
    mediaQualityAssurance: null,
    mobileDevicesConnectivity: null,
    retargeting: null,
    supplySource: null,
    temperatureAndWeather: withStatus(
      checkIfConfigured(getTemperatureAndWeatherTags(temperatureAndWeather)),
      'temperatureAndWeather'
    )
  } satisfies DotStatusesByTargeting;

  return type === CampaignFamilyEnum.dooh
    ? { ...baseDotStatuses, demography: withStatus(Boolean(doohAudience && isDoohAudienceConfigured(doohAudience))) }
    : {
        ...baseDotStatuses,
        appSiteCategories: withStatus(categories?.length > 0, 'categories'),
        appSiteChannel: withStatus(checkIfConfigured(getAppAndSiteChannelTags(channels)), 'channels'),
        audienceTargeting: withStatus(checkIfConfigured(getAudienceTargetingTags(audience?.lists)), 'audience'),
        contentCategories: withStatus(
          Boolean(contextualTargeting?.iabCategories?.enabled) || Boolean(contextualTargeting?.iasCategories?.enabled),
          'contextualTargeting'
        ),
        demography: withStatus(
          checkIfConfigured(getDemographyTags(ages, gender, languages)),
          'ages',
          'gender',
          'languages'
        ),
        deviceTargeting: withStatus(checkIfConfigured(getDeviceTargetingTags(targetUsers)), 'targetUsers'),
        locationQualityIndexes: withStatus(checkIfConfigured(getLQITags(lqi)), 'lqi'),
        mediaQualityAssurance: withStatus(checkIfConfigured(getMediaQualityTags(mediaQuality)), 'mediaQuality'),
        mobileDevicesConnectivity: withStatus(
          checkIfConfigured(getMobileDeviceTags(deviceTypes, deviceOsV2, connectionTypes)),
          'deviceTypes',
          'deviceOsV2',
          'connectionTypes'
        ),
        retargeting: withStatus(retargeting?.enabled === true, 'retargeting'),
        supplySource: withStatus(checkIfConfigured(getSupplySourceTags(adExchanges)), 'adExchanges')
      };
}
